import {
  Box,
  Flex,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';

import FigmaFilterIcon from 'components/Icons/FigmaFilterIcon';
import { getDimensionFilters, handleDimFiltersChange } from 'redux/ItemPageSlice';
import { radius } from 'styles/revampTokens';

interface Properties {
  dimensionProperties: any[];
  setFiltersData: (data: any) => void;
  /** Icon-only mode — used when a side panel is open and header space is tight. */
  compact?: boolean;
}

// Summary panel for the header "Filters" button — lists every column that
// currently has an active filter (dimensionFilters[i].isFiltersApplied) so
// the user has one place to see/clear filters instead of hunting through
// each column's own funnel icon. Reuses the exact same handleDimFiltersChange
// action the per-column filter popup (components/FiltersCell/Filters.tsx)
// already dispatches — no new filtering logic, just a different UI on top
// of the same state.
const ActiveFiltersPopover = ({ dimensionProperties, setFiltersData, compact }: Properties) => {
  const dispatch: any = useDispatch();
  const dimensionFilters: any = useSelector(getDimensionFilters);

  const activeFilters = (dimensionFilters || []).filter((filter: any) => filter?.isFiltersApplied);

  const getLabel = (filterId: string) => {
    if (filterId === 'name') return 'Name';
    const property = dimensionProperties?.find((item) => String(item?.id) === String(filterId));
    return property?.name || filterId;
  };

  const clearFilter = (filter: any) => {
    const clearedData = filter?.data?.map((item: any) => ({ name: item?.name, status: true }));
    dispatch(handleDimFiltersChange({ id: filter?.id, ischeckedItemsChecked: clearedData }));
    setFiltersData({ cell: { id: filter?.id }, data: clearedData });
  };

  const clearAllFilters = () => {
    activeFilters.forEach((filter: any) => {
      const clearedData = filter?.data?.map((item: any) => ({ name: item?.name, status: true }));
      dispatch(handleDimFiltersChange({ id: filter?.id, ischeckedItemsChecked: clearedData }));
    });
    setFiltersData({ cell: {}, data: [] });
  };

  return (
    <Popover placement='bottom-start' isLazy>
      {({ isOpen }) => (
        <>
          <PopoverTrigger>
            <Flex
              as='button'
              alignItems='center'
              gap='8px'
              bg={activeFilters.length > 0 ? '#F5F0FF' : 'white'}
              border={activeFilters.length > 0 ? '1px solid #8B5CF6' : '1px solid #D1D6DD'}
              borderRadius={radius.control}
              px={compact ? '9px' : '12px'}
              height='32px'
              cursor='pointer'
              flexShrink={0}
              transition='background 0.15s ease, transform 0.1s ease'
              _hover={{ bg: '#F5F0FF' }}
              _active={{ bg: '#F5F0FF', transform: 'scale(0.97)' }}
            >
              <FigmaFilterIcon
                width={14}
                height={14}
                color={activeFilters.length > 0 ? '#8B5CF6' : '#121826'}
              />
              {(!compact || activeFilters.length > 0) && (
                <Text
                  fontSize='13px'
                  fontWeight='500'
                  fontFamily='Roboto Medium, Roboto Regular'
                  color={activeFilters.length > 0 ? '#8B5CF6' : '#121826'}
                  whiteSpace='nowrap'
                >
                  {compact
                    ? activeFilters.length
                    : `Filters${activeFilters.length > 0 ? ` (${activeFilters.length})` : ''}`}
                </Text>
              )}
            </Flex>
          </PopoverTrigger>
          {isOpen && (
            <PopoverContent
              width='260px'
              bg='white'
              border='1px solid #D1D6DD'
              borderRadius={radius.overlay}
              overflow='hidden'
              boxShadow='0px 8px 24px rgba(18, 24, 38, 0.12)'
              p={0}
              _focusVisible={{ boxShadow: '0px 8px 24px rgba(18, 24, 38, 0.12)' }}
            >
              <PopoverBody p={0}>
                <Flex
                  alignItems='center'
                  justifyContent='space-between'
                  px='12px'
                  py='12px'
                  borderBottom='1px solid #D1D6DD'
                >
                  <Text
                    fontSize='13px'
                    fontWeight='500'
                    fontFamily='Roboto Medium, Roboto Regular'
                    color='#121826'
                  >
                    Active Filters
                  </Text>
                  {activeFilters.length > 0 && (
                    <Text
                      as='button'
                      onClick={clearAllFilters}
                      fontSize='12px'
                      fontWeight='500'
                      fontFamily='Roboto Medium, Roboto Regular'
                      color='#8B5CF6'
                      cursor='pointer'
                      _hover={{ textDecoration: 'underline' }}
                    >
                      Clear all
                    </Text>
                  )}
                </Flex>
                {activeFilters.length === 0 ? (
                  <Box px='12px' py='16px'>
                    <Text
                      fontSize='12px'
                      fontFamily='Roboto Regular'
                      color='#708090'
                      textAlign='center'
                    >
                      No filters applied yet. Use the funnel icon on any column header to filter
                      items.
                    </Text>
                  </Box>
                ) : (
                  <Flex direction='column' gap='1px' bg='#E8E9EB' maxH='220px' overflowY='auto'>
                    {activeFilters.map((filter: any) => {
                      const selectedCount = filter?.data?.filter(
                        (item: any) => item?.status,
                      )?.length;
                      return (
                        <Flex
                          key={filter?.id}
                          alignItems='center'
                          justifyContent='space-between'
                          bg='white'
                          px='12px'
                          py='8px'
                          gap='8px'
                        >
                          <Box minW={0}>
                            <Text
                              fontSize='13px'
                              fontFamily='Roboto Regular'
                              color='#121826'
                              whiteSpace='nowrap'
                              overflow='hidden'
                              textOverflow='ellipsis'
                            >
                              {getLabel(filter?.id)}
                            </Text>
                            <Text fontSize='11px' fontFamily='Roboto Regular' color='#708090'>
                              {selectedCount} selected
                            </Text>
                          </Box>
                          <Box
                            as='button'
                            onClick={() => clearFilter(filter)}
                            display='flex'
                            alignItems='center'
                            justifyContent='center'
                            width='18px'
                            height='18px'
                            borderRadius={radius.control}
                            flexShrink={0}
                            transition='background 0.15s ease'
                            _hover={{ bg: '#FEF0F0' }}
                          >
                            <CloseIcon boxSize='7px' color='#708090' />
                          </Box>
                        </Flex>
                      );
                    })}
                  </Flex>
                )}
              </PopoverBody>
            </PopoverContent>
          )}
        </>
      )}
    </Popover>
  );
};

export default ActiveFiltersPopover;
